import { useLang } from 'hooks/lang';

import { IconButton, ResponsiveOverlay, Row, Tabs } from 'components/Blocks';
import { EntityStatusTag } from 'components/Entities';
import { CalendarRangeIcon, RefreshIcon } from 'components/Icons';

import { formatDate } from 'utils/date';

import { RecruitmentOwner } from '../RecruitmentOwner';
import { RecruitmentComments } from './Comments';
import { RecruitmentEvents } from './Events';
import { LoadingRecruitment } from './Loading';
import { MainInfo } from './Main';
import { FinishRecruitment } from './Finish';
import { DenyRecruitment } from './Deny';
import { AssignButton } from './AssignButton';

import { RecruitmentTab } from './types';

import {
  RecruitmentModalContextProps,
  useRecruitmentModal,
  withRecruitmentModalContext,
} from './context';

interface RecruitmentModalProps extends RecruitmentModalContextProps {
  children?: never;
}

const tabComponents: Record<RecruitmentTab, typeof MainInfo> = {
  main: MainInfo,

  logs: RecruitmentEvents,

  comments: RecruitmentComments,

  finish: FinishRecruitment,

  deny: DenyRecruitment,
};

function RecruitmentModalInner({ recruitmentId }: RecruitmentModalProps): JSX.Element {
  const { t } = useLang();

  const { tab, recruitment, close, reload, updateRecruitment, setTab, permissions } =
    useRecruitmentModal();

  const Component = tabComponents[tab];

  const tabs = [
    { value: 'main', label: t('main')! },
    { value: 'logs', label: t('logs')! },
    {
      value: 'comments',
      label: `${t('comments')} (${recruitment?.comments.length})`,
    },
    {
      value: 'finish',
      label: t('finish')!,
      render: permissions.canFinish,
    },
    {
      value: 'deny',
      label: t('deny')!,
      render: permissions.canDeny,
    },
  ].filter((r) => r.render ?? true);

  return (
    <ResponsiveOverlay
      isOpen={!!recruitmentId}
      close={close}
      width="min(90vw, 700px)"
      className="h-[min(800px,82vh)] max-h-[82vh] overflow-y-auto"
      allowEscape
      title={
        <Row gap="2">
          <h2 className="text-lg font-semibold leading-none tracking-tight">{t('recruitment')}</h2>

          <IconButton onClick={reload}>
            <RefreshIcon size={16} />
          </IconButton>
        </Row>
      }
    >
      {!recruitment ? (
        <LoadingRecruitment />
      ) : (
        <>
          <Row justifyContent="space-between">
            <Row gap="2">
              <RecruitmentOwner
                recruitment={recruitment}
                showName={false}
                updateRecruitment={updateRecruitment}
              />

              <EntityStatusTag
                target="recruitments"
                status={recruitment.status}
                round={false}
                size="medium"
              />

              <AssignButton />
            </Row>

            <Row gap="2">
              <CalendarRangeIcon size={20} />

              {formatDate(recruitment.requestedAt)}
            </Row>
          </Row>

          <Tabs
            value={tab}
            onTabChange={setTab as () => void}
            tabs={tabs}
            tabListClassName="-ml-2"
          />

          {Component && <Component />}
        </>
      )}
    </ResponsiveOverlay>
  );
}

export const RecruitmentModal = withRecruitmentModalContext(RecruitmentModalInner);
